import * as React from "react";
import { Fingerprint, ScanFace, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

export type BiometricType = "fingerprint" | "face" | "none";

export interface BiometricButtonProps {
  type?: BiometricType;
  onPress: () => void;
  loading?: boolean;
  disabled?: boolean;
  error?: string;
  success?: boolean;
  className?: string;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
}

export const BiometricButton: React.FC<BiometricButtonProps> = ({
  type = "fingerprint",
  onPress,
  loading = false,
  disabled = false,
  error,
  success = false,
  className,
  size = "lg",
  showLabel = true,
}) => {
  const { t } = useTranslation();

  const Icon = type === "face" ? ScanFace : Fingerprint;

  const sizeClasses = {
    sm: "w-16 h-16",
    md: "w-20 h-20",
    lg: "w-24 h-24",
  };

  const iconSizes = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-12 h-12",
  };

  const label = type === "face" 
    ? t("auth.useFaceId", "Use Face ID") 
    : t("auth.useFingerprint", "Use Fingerprint");

  return (
    <div className={cn("flex flex-col items-center gap-3", className)}>
      <motion.div
        whileTap={{ scale: 0.95 }}
        animate={success ? { scale: [1, 1.1, 1] } : {}}
      >
        <Button
          variant="outline"
          onClick={onPress}
          disabled={disabled || loading}
          className={cn(
            sizeClasses[size],
            "rounded-full border-2 transition-all duration-300",
            "hover:bg-primary/5 hover:border-primary",
            "active:scale-95",
            success && "border-green-500 bg-green-500/10",
            error && "border-destructive bg-destructive/10",
            !success && !error && "border-muted-foreground/30"
          )}
        >
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
              >
                <Loader2 className={cn(iconSizes[size], "animate-spin text-primary")} />
              </motion.div>
            ) : (
              <motion.div
                key="icon"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
              >
                <Icon
                  className={cn(
                    iconSizes[size],
                    success ? "text-green-500" : error ? "text-destructive" : "text-primary"
                  )}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </Button>
      </motion.div>

      {showLabel && (
        <span className={cn(
          "text-sm font-medium simple-hide",
          success ? "text-green-600" : error ? "text-destructive" : "text-muted-foreground"
        )}>
          {error || (success ? t("auth.verified", "Verified!") : label)}
        </span>
      )}

      {/* Pulse animation ring */}
      {!disabled && !loading && !success && (
        <motion.div
          className={cn(
            "absolute rounded-full border-2 border-primary/30 pointer-events-none",
            sizeClasses[size]
          )}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0, 0.5],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      )}
    </div>
  );
};

// Hook to detect available biometric type (placeholder for native integration)
export const useBiometricType = (): BiometricType => {
  const [type, setType] = React.useState<BiometricType>("none");

  React.useEffect(() => {
    // Check for Web Authentication API availability
    if (window.PublicKeyCredential) {
      // Default to fingerprint for most mobile devices
      // In a real app, this would check device capabilities
      setType("fingerprint");
    }
  }, []);

  return type;
};

export default BiometricButton;
